import { fetchApi } from '@/services/api';

import type { ScopeOption } from '../hooks/useScopeFilterOptions';

function parseProductCodeFromLabel(label: string): string {
  const idx = label.indexOf(' - ');
  return idx > 0 ? label.slice(0, idx).trim() : label.trim();
}

export async function fetchProductScopeOptions(query: string): Promise<ScopeOption[]> {
  const q = query.trim();
  const data = await fetchApi<{ product?: Array<{ value: number; label: string }> }>(
    'products/filter-options/',
    {
      params: {
        module: 'predictions',
        products_mode: q.length >= 1 ? 'search' : 'slice',
        ...(q.length >= 1 ? { product_q: q } : {}),
      },
    },
  );
  return (data.product ?? []).map((row) => {
    const code = parseProductCodeFromLabel(row.label);
    return { code, label: row.label, id: row.value };
  });
}

async function resolveProductCodeToId(code: string): Promise<number | null> {
  const trimmed = code.trim();
  if (!trimmed) return null;

  const opts = await fetchProductScopeOptions(trimmed);
  const exact = opts.find((o) => o.code === trimmed);
  if (exact?.id != null) return exact.id;

  try {
    const data = await fetchApi<{ results?: Array<{ id: number; code: string }> }>(
      'products/products/',
      { params: { search: trimmed, page_size: 10 } },
    );
    const hit = data.results?.find((p) => p.code === trimmed);
    return hit?.id ?? null;
  } catch {
    return null;
  }
}

export async function fetchProductCodeById(productId: number): Promise<string | null> {
  try {
    const p = await fetchApi<{ code?: string; client_code?: string }>(
      `products/products/${productId}/`,
    );
    // Mismo criterio que filter-options (usa `code` en la etiqueta del selector).
    return p.code ?? p.client_code ?? null;
  } catch {
    return null;
  }
}

export async function fetchProductLabelById(productId: number): Promise<string | null> {
  try {
    const p = await fetchApi<{ code?: string; client_code?: string; name?: string }>(
      `products/products/${productId}/`,
    );
    const code = p.code ?? p.client_code;
    if (!code) return null;
    return p.name ? `${code} - ${p.name}` : code;
  } catch {
    return null;
  }
}
export async function resolveProductCodesToIds(codes: string[]): Promise<Map<string, number>> {
  const unique = [...new Set(codes.map((c) => c.trim()).filter(Boolean))];
  const map = new Map<string, number>();
  if (!unique.length) return map;

  await Promise.all(
    unique.map(async (code) => {
      const id = await resolveProductCodeToId(code);
      if (id != null) map.set(code, id);
    }),
  );
  return map;
}
